/**
 * Built-in page translation (Safari, Chrome, the Google app) replaces text nodes under parents React
 * owns. React's next commit then removes or inserts against a parent that no longer holds the node and
 * the DOM throws NotFoundError, which takes the whole render down to an error boundary. Guarding both
 * mutators keeps the page alive for translated users: facebook/react#11538, KITT-PORTAL-128.
 */

export type TDomGuardOperation = 'removeChild' | 'insertBefore';

export type TOnDomGuarded = (operation: TDomGuardOperation) => void;

let isInstalled = false;

export function createGuardedRemoveChild(
  originalRemoveChild: typeof Node.prototype.removeChild,
  onGuarded: TOnDomGuarded,
): typeof Node.prototype.removeChild {
  return function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    const actualParent = child.parentNode;

    if (actualParent === this) {
      return originalRemoveChild.call(this, child) as T;
    }

    if (actualParent == null) {
      onGuarded('removeChild');
      return child;
    }

    // translator wrapped the child (e.g. Chrome's <font>); React drops its reference after this commit
    if (this.contains(actualParent)) {
      onGuarded('removeChild');
      return originalRemoveChild.call(actualParent, child) as T;
    }

    return originalRemoveChild.call(this, child) as T;
  };
}

export function createGuardedInsertBefore(
  originalInsertBefore: typeof Node.prototype.insertBefore,
  onGuarded: TOnDomGuarded,
): typeof Node.prototype.insertBefore {
  return function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    referenceNode: Nullable<Node>,
  ): T {
    const referenceParent = referenceNode?.parentNode ?? null;

    if (referenceNode == null || referenceParent === this) {
      return originalInsertBefore.call(this, node, referenceNode) as T;
    }

    // insert into the translator's wrapper to keep React's ordering
    if (referenceParent != null && this.contains(referenceParent)) {
      onGuarded('insertBefore');
      return originalInsertBefore.call(referenceParent, node, referenceNode) as T;
    }

    return originalInsertBefore.call(this, node, referenceNode) as T;
  };
}

export function installTranslatedDomGuard(onGuarded: TOnDomGuarded): void {
  if (isInstalled || typeof Node === 'undefined') {
    return;
  }

  isInstalled = true;

  Node.prototype.removeChild = createGuardedRemoveChild(Node.prototype.removeChild, onGuarded);
  Node.prototype.insertBefore = createGuardedInsertBefore(Node.prototype.insertBefore, onGuarded);
}
